package com.example.mystybag.login.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.biometricUtils.helpers.BiometricAuthenticationType
import com.example.mystybag.common.extensions.AsyncImage
import com.example.mystybag.communication.defines.CommunicationDefines.Companion.DOMAIN
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.noRippleClickable
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.defines.TextFieldType
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.login.helpers.navigateToLoginHelp
import com.example.mystybag.login.logic.StaffLoginViewModel
import com.example.mystybag.login.views.components.MerchantRegistrationWebContentColumnWidth
import com.example.mystybag.login.views.components.WebInlineLanguageSwitcher
import dev.icerock.moko.resources.compose.painterResource
import dev.icerock.moko.resources.compose.stringResource

private val WELCOME_LOGO_MARK: String
    get() = "https://$DOMAIN/static/email_photos/logo_40.png"

@Composable
fun StaffLoginViewWeb(viewModel: StaffLoginViewModel) {
    val keyboardController = LocalSoftwareKeyboardController.current
    val theme = AppTheme.current
    val uiState = viewModel.uiState.subscribeAsState()

    Box(modifier = Modifier.fillMaxSize()) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            Column(
                modifier = Modifier.width(MerchantRegistrationWebContentColumnWidth),
                verticalArrangement = Arrangement.spacedBy(48.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(53.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = stringResource(SR.strings.login_as_staff),
                        color = theme.colors.onSurface,
                        fontFamily = LocalTheme.googleSans,
                        fontSize = 48.sp,
                        lineHeight = 52.8.sp,
                        fontWeight = FontWeight.Bold,
                        textAlign = TextAlign.Center,
                        modifier = Modifier.fillMaxWidth()
                    )
                }

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(24.dp)
                ) {
                    DefaultCircleShapedTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = uiState.value.email,
                        label = stringResource(SR.strings.email_address),
                        errorText = uiState.value.emailErrorMsg,
                        onValueChange = viewModel::onEmailChange,
                        isError = uiState.value.emailIsError,
                        inputType = TextFieldType.EMAIL,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Email,
                            imeAction = ImeAction.Next
                        )
                    )
                    DefaultCircleShapedTextField(
                        modifier = Modifier.fillMaxWidth(),
                        value = uiState.value.password,
                        label = stringResource(SR.strings.password),
                        errorText = uiState.value.passwordErrorMsg,
                        onValueChange = viewModel::onPasswordChange,
                        isError = uiState.value.passwordIsError,
                        visualTransformation = if (uiState.value.passwordVisible) {
                            VisualTransformation.None
                        } else {
                            PasswordVisualTransformation()
                        },
                        trailingIcon = {
                            Image(
                                painter = painterResource(
                                    if (uiState.value.passwordVisible) SRC.images.password_eye_open else SRC.images.password_eye_closed
                                ),
                                modifier = Modifier
                                    .size(p24)
                                    .noRippleClickable { viewModel.togglePasswordVisibility() }
                            )
                        },
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Password,
                            imeAction = ImeAction.Done
                        )
                    )
                    Text(
                        text = stringResource(SR.strings.forgot_password_question),
                        color = theme.colors.primary,
                        style = theme.typography.bodyMedium,
                        textDecoration = TextDecoration.Underline,
                        textAlign = TextAlign.Center,
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(20.dp)
                            .noRippleClickable { viewModel.onForgotPasswordClicked() }
                    )
                }

                Column(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p32),
                    verticalArrangement = Arrangement.spacedBy(24.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    if (uiState.value.showBiometricLogin) {
                        val biometricCaption = when (uiState.value.biometricType) {
                            BiometricAuthenticationType.FACE -> stringResource(SR.strings.login_with_faceID)
                            BiometricAuthenticationType.FINGERPRINT -> stringResource(SR.strings.login_with_fingerprint)
                            else -> ""
                        }
                        if (biometricCaption.isNotEmpty()) {
                            DefaultButton(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .padding(bottom = p8),
                                caption = biometricCaption,
                                type = DefaultButtonType.OUTLINED,
                                callback = {
                                    keyboardController?.hide()
                                    viewModel.onBiometricLoginClicked()
                                },
                                identifier = "Staff Web Biometric Login"
                            )
                        }
                    }

                    DefaultButton(
                        modifier = Modifier.fillMaxWidth(),
                        caption = stringResource(SR.strings.sign_in),
                        callback = {
                            keyboardController?.hide()
                            viewModel.onLoginClicked()
                        },
                        identifier = "Staff Web Login"
                    )
                }
            }
        }

        Box(
            modifier = Modifier
                .align(Alignment.TopCenter)
                .fillMaxWidth()
                .height(120.dp)
                .background(theme.colors.surface),
            contentAlignment = Alignment.Center
        ) {
            Row(
                modifier = Modifier
                    .width(MerchantRegistrationWebContentColumnWidth)
                    .padding(vertical = 32.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(20.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(48.dp)
                            .clip(CircleShape)
                            .background(theme.colors.surfaceContainerLowest)
                            .noRippleClickable { viewModel.onBackButtonClicked() },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            painter = painterResource(SRC.images.arrow_back),
                            modifier = Modifier.size(18.dp)
                        )
                    }
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        AsyncImage(
                            link = WELCOME_LOGO_MARK,
                            placeholder = null,
                            modifier = Modifier
                                .width(40.dp)
                                .height(40.dp)
                                .clip(RoundedCornerShape(10.dp)),
                            contentScale = ContentScale.Fit
                        )
                        Text(
                            text = "MystyBag",
                            color = theme.colors.onSurface,
                            fontFamily = LocalTheme.googleSans,
                            fontSize = 24.sp,
                            lineHeight = 40.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
                Row(
                    horizontalArrangement = Arrangement.spacedBy(24.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Texts.BodyMedium(
                        text = stringResource(SR.strings.help_center),
                        color = theme.colors.onSurfaceVariant,
                        modifier = Modifier.noRippleClickable { viewModel.navigateToLoginHelp() }
                    )
                    WebInlineLanguageSwitcher()
                }
            }
        }
    }
}
